// Turns "which buttons are held this frame" into game actions, with the
// usual hold-to-repeat on left/right: one step on press, then after a short
// pause, a quick steady slide.
//
// Pure: no timers, no DOM. The game loop calls it once per frame with the
// held buttons and the frame time.

// Tuned on 2026-10-01 with Matt on two DualSense pads. Was 170/50 ("doesn't
// feel ... quite as snappy"), then 100/25 - but 100ms is about as long as a
// quick tap lasts, so a fast double-tap sometimes counted as a short hold
// and moved an extra cell ("I'm getting an extra tap. It's annoying"). The
// pause before the slide is back to 150ms; the slide itself stays fast.
export const REPEAT_DELAY_MS = 150;
export const REPEAT_RATE_MS = 25;
// A release and re-press closer together than this is the controller's
// wireless link flickering, not a person tapping - nobody taps that fast.
export const BOUNCE_MS = 25;

// `alreadyHeld`: buttons that are down at the moment this starts counting.
// They are NOT presses. Without this, the Start press that resumed a paused
// game was read again by the fresh repeater and paused it straight back
// (Matt, 2026-10-01: "once I pause the game, there doesn't seem to be any
// way to unpause it"), and the drop that starts a game slammed the first
// piece down.
export function createRepeater(alreadyHeld = []) {
  return {
    prev: new Set(alreadyHeld), side: null, sideMs: 0, repeatMs: 0, lastSide: null, sinceSideMs: Infinity,
  };
}

export function readActions(rep, held, dtMs) {
  const actions = [];
  const pressed = (b) => held.has(b) && !rep.prev.has(b);

  if (pressed('cw')) actions.push('rotateCW');
  if (pressed('ccw')) actions.push('rotateCCW');

  // Sideways: the most recent direction wins if both are held.
  let side = null;
  if (held.has('left') && held.has('right')) {
    side = pressed('left') ? 'left' : pressed('right') ? 'right' : rep.side;
  } else if (held.has('left')) side = 'left';
  else if (held.has('right')) side = 'right';

  if (side && side !== rep.side) {
    // Back on the same direction within a blink of letting go: a bounce.
    // Carry on as if it had been held all along - no extra cell.
    const bounce = !rep.side && side === rep.lastSide && rep.sinceSideMs < BOUNCE_MS;
    if (!bounce) {
      actions.push(side);
      rep.sideMs = 0;
      rep.repeatMs = 0;
    }
  } else if (side) {
    rep.sideMs += dtMs;
    if (rep.sideMs >= REPEAT_DELAY_MS) {
      rep.repeatMs += dtMs;
      while (rep.repeatMs >= REPEAT_RATE_MS) {
        rep.repeatMs -= REPEAT_RATE_MS;
        actions.push(side);
      }
    }
  }
  if (side) {
    rep.lastSide = side;
    rep.sinceSideMs = 0;
  } else {
    rep.sinceSideMs += dtMs;
  }
  rep.side = side;

  // Down is one row per press - no holding. Matt, after the first real
  // playtest: holding it was so fast "it's basically the same" as a drop;
  // "tap, tap, tap, tap, tap, and you can move down one at a time." It's
  // for slipping out of someone's way, so it has to be that precise.
  if (pressed('down')) actions.push('stepDown');
  if (pressed('drop')) actions.push('hardDrop');
  if (pressed('start')) actions.push('start');

  rep.prev = new Set(held);
  return actions;
}
